import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { Popover, PopoverModule } from 'primeng/popover';
import { SkeletonModule } from 'primeng/skeleton';
import { timer } from 'rxjs';
import { MessageDetailDialogComponent } from '../message-detail-dialog/message-detail-dialog.component';
import { Message } from '../../models/message.model';
import { MessagesApiService } from '../../services/messages-api.service';
import {
  messageChangesList,
  messageFieldLabel,
  messageSnapshotList,
  messageTitleKey,
  messageTitleParams,
} from '../../utils/message-content.util';
import { SERVICE_PACKAGE_TYPE_LABELS } from '../../../service-packages/models/service-package.model';

const MESSAGES_PAGE_SIZE = 20;
const UNREAD_POLL_INTERVAL_MS = 10 * 60 * 1000;

@Component({
  selector: 'cam-messages-inbox',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    ButtonModule,
    PopoverModule,
    SkeletonModule,
    MessageDetailDialogComponent,
  ],
  templateUrl: './messages-inbox.component.html',
  providers: [DatePipe, CurrencyPipe],
})
export class MessagesInboxComponent {
  private readonly messagesApi = inject(MessagesApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translateService = inject(TranslateService);
  private readonly datePipe = inject(DatePipe);
  private readonly currencyPipe = inject(CurrencyPipe);

  protected hasUnread = signal(false);
  protected messages = signal<Message[]>([]);
  protected messagesLoading = signal(false);
  protected messagesError = signal(false);
  protected detailDialogVisible = signal(false);
  protected selectedMessage = signal<Message | undefined>(undefined);

  private _currentPage = signal(1);
  private _lastPage = signal(1);
  protected hasMore = computed(() => this._lastPage() > this._currentPage());

  constructor() {
    timer(0, UNREAD_POLL_INTERVAL_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this._checkUnreadCount());
  }

  private _checkUnreadCount(): void {
    this.messagesApi.list({ onlyUnread: true, perPage: 1 }).subscribe({
      next: (response) => this.hasUnread.set(response.total > 0),
    });
  }

  private _refreshMessages(): void {
    this._currentPage.set(1);
    this._lastPage.set(1);
    this._loadPage(1);
  }

  private _loadPage(page: number): void {
    this.messagesLoading.set(true);
    this.messagesError.set(false);

    this.messagesApi
      .list({ onlyUnread: true, page, perPage: MESSAGES_PAGE_SIZE })
      .subscribe({
        next: (response) => {
          this.messages.update((current) =>
            page === 1 ? response.items : [...current, ...response.items],
          );
          this._currentPage.set(response.currentPage);
          this._lastPage.set(response.lastPage);
          this.hasUnread.set(response.total > 0);
          this.messagesLoading.set(false);
        },
        error: () => {
          this.messagesError.set(true);
          this.messagesLoading.set(false);
        },
      });
  }

  protected onPopoverShow(): void {
    this._refreshMessages();
  }

  protected loadMore(): void {
    this._loadPage(this._currentPage() + 1);
  }

  protected openMessage(message: Message, popover: Popover): void {
    this.selectedMessage.set(message);
    this.detailDialogVisible.set(true);
    popover.hide();
  }

  protected onMessageRead(): void {
    this._refreshMessages();
  }

  protected getTitle(message: Message): string {
    return this.translateService.instant(
      messageTitleKey(message.type),
      messageTitleParams(message),
    );
  }

  protected getSummary(message: Message): string {
    if (message.content.snapshot) {
      return messageSnapshotList(message.content)
        .map(
          (item) =>
            `${this.translateService.instant(messageFieldLabel(item.field))}: ${this._formatChangeValue(item.field, item.value)}`,
        )
        .join(', ');
    }

    return messageChangesList(message.content)
      .map(
        (change) =>
          `${this.translateService.instant(messageFieldLabel(change.field))}: ${this._formatChangeValue(change.field, change.from)} → ${this._formatChangeValue(change.field, change.to)}`,
      )
      .join(', ');
  }

  protected getMeta(message: Message): string {
    const parts: string[] = [];

    if (message.content.changedByName) {
      parts.push(message.content.changedByName);
    }

    const formattedDate = this.datePipe.transform(
      message.createdAt,
      'dd/MM/yyyy HH:mm',
    );
    if (formattedDate) parts.push(formattedDate);

    return parts.join(' · ');
  }

  private _formatChangeValue(field: string, value: unknown): string {
    if (value === null || value === undefined) return '—';

    if (field === 'start' || field === 'finish') {
      return (
        this.datePipe.transform(value as string, 'dd/MM/yyyy', 'UTC') ?? '—'
      );
    }

    if (field === 'value') {
      return (
        this.currencyPipe.transform(
          value as number | string,
          'BRL',
          'symbol',
          '1.2-2',
          'pt-BR',
        ) ?? '—'
      );
    }

    if (field === 'type') {
      const labelKey = (SERVICE_PACKAGE_TYPE_LABELS as Record<string, string>)[
        value as string
      ];
      return labelKey ? this.translateService.instant(labelKey) : String(value);
    }

    return String(value);
  }
}
